import { useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';
import { useNavigate } from 'react-router';
import { Form, FormikProvider, useFormik } from 'formik';

import useAuth from 'src/hooks/useAuth';
// material
import { Alert, Button, OutlinedInput, Stack } from '@mui/material';
import axios from 'src/utils/axios';
import TermsOfServiceDialogV2 from 'src/components/authentication/login/V2/TermsOfServiceDialogV2';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import { clearPendingPhoneLogin } from 'src/components/authentication/login/V2/pendingPhoneLogin';

// ----------------------------------------------------------------------

function maxLength(object) {
  if (object.target.value.length > object.target.maxLength) {
    return (object.target.value = object.target.value.slice(
      0,
      object.target.maxLength
    ));
  }
}

export default function VerifyCodeForm({
  settings,
  handleSave,
  onExtendSessionSuccess,
  onLoginSuccess,
  returnUrl,
  skipTermsOfService = false,
}) {
  const { login, extendCurrentSession, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isMountedRef = useIsMountedRef();
  const termsOfServiceRef = useRef(null);

  const [sessionDetails, setSessionDetails] = useState(null);

  const navigateAfterLoginRef = useRef(null);
  navigateAfterLoginRef.current = () => {
    if (onLoginSuccess) {
      onLoginSuccess();
    } else if (returnUrl && returnUrl.startsWith('/')) {
      navigate(returnUrl);
    } else {
      navigate('/');
    }
  };

  const navigateAfterLogin = () => navigateAfterLoginRef.current();

  const VerifyCodeSchema = Yup.object().shape({
    code0: Yup.number().required('Code is required'),
    code1: Yup.number().required('Code is required'),
    code2: Yup.number().required('Code is required'),
    code3: Yup.number().required('Code is required'),
    code4: Yup.number().required('Code is required'),
    code5: Yup.number().required('Code is required'),
  });

  const formik = useFormik({
    initialValues: {
      code0: '',
      code1: '',
      code2: '',
      code3: '',
      code4: '',
      code5: '',
    },
    validationSchema: VerifyCodeSchema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      try {
        const code = Object.values(values)
          .map((item) => item)
          .join('');
        // Use new endpoint that works with User.phone_number
        const { data } = await axios.post(
          !settings
            ? 'user_phone_login/verify_code/'
            : 'user_phone_verification/verify_mobile/',
          { code }
        );

        if (!settings) {
          // ENG-4123: the code is consumed server-side by this call
          // (verify_code deletes the row), so there is no pending phone login
          // left for a reload to restore.
          clearPendingPhoneLogin();

          const { access, refresh } = data;

          if (isAuthenticated) {
            extendCurrentSession(data);
            if (onExtendSessionSuccess) {
              onExtendSessionSuccess(data);
            }
            closeDialog();
            return;
          }

          if (!skipTermsOfService) {
            const { data: user } = await axios.get(
              'user_profile/user/current_profile/'
            );

            if (!user.terms_of_service_agreement) {
              setSessionDetails({
                user: user.id,
                access: access,
                refresh: refresh,
              });
              termsOfServiceRef.current?.toggleOpenDialog();
              return;
            }
          }

          await login(access, refresh);
          navigateAfterLogin();
        } else {
          handleSave();
        }

        if (isMountedRef.current) {
          setSubmitting(false);
          resetForm();
        }
      } catch (error) {
        console.error(error);
        if (isMountedRef.current) {
          setErrors({ afterSubmit: error?.detail });
          setSubmitting(false);
        }
        setErrors({
          afterSubmit:
            'Invalid code, review the information or request another code.',
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setErrors,
    setFieldValue,
    resetForm,
  } = formik;

  const handlePaste = (e) => {
    const copiedCode = e.clipboardData.getData('Text');
    [...copiedCode].forEach((c, idx) => {
      if (idx >= 6) {
        return true;
      }
      setFieldValue(`code${idx}`, c);
    });
  };

  const isValidForm = useMemo(() => {
    return Object.values(values).every((v) => Boolean(v) || v === 0);
  }, [values]);

  useEffect(() => {
    document.getElementById('code0').focus();
  }, []);

  const closeDialog = () => {
    localStorage.removeItem('temporalSession');
    termsOfServiceRef.current?.toggleOpenDialog();
  };

  const onAccept = async () => {
    try {
      await axios.patch(`user_profile/user/${sessionDetails?.user}/`, {
        terms_of_service_agreement: true,
      });

      await login(sessionDetails.access, sessionDetails.refresh);
      closeDialog();
      navigateAfterLogin();
    } catch (error) {
      console.error(error);
      setErrors({ afterSubmit: 'Something went wrong.' });
      closeDialog();
    }
  };

  return (
    <>
      <FormikProvider value={formik}>
        {errors.afterSubmit && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.afterSubmit}
          </Alert>
        )}
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: 'center',
            }}
          >
            {Object.keys(values).map((item, idx) => {
              return (
                <OutlinedInput
                  key={item}
                  id={item}
                  {...getFieldProps(item)}
                  onPaste={(e) => handlePaste(e)}
                  onChange={(e) => {
                    if (isNaN(e.target.value)) {
                      return;
                    }
                    if (
                      idx < Object.keys(values).length - 1 &&
                      e.target.value !== ''
                    ) {
                      document.getElementById('code' + (idx + 1)).focus();
                    }
                    setFieldValue(item, e.target.value);
                  }}
                  onKeyUp={(e) => {
                    if (e.key === 'Backspace' && idx > 0) {
                      document.getElementById('code' + (idx - 1)).focus();
                    }
                  }}
                  type="number"
                  placeholder="-"
                  onInput={maxLength}
                  error={Boolean(touched[item] && errors[item])}
                  inputProps={{
                    maxLength: 1,
                    sx: {
                      p: 0,
                      textAlign: 'center',
                      width: { xs: 36, sm: 56 },
                      height: { xs: 36, sm: 56 },
                    },
                  }}
                />
              );
            })}
          </Stack>
          <Button
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            disabled={!isValidForm}
            loading={isSubmitting}
            sx={{ mt: 3 }}
          >
            {!settings ? 'Login' : 'Verify Code'}
          </Button>
        </Form>
      </FormikProvider>
      <TermsOfServiceDialogV2
        onCancel={closeDialog}
        onAccept={onAccept}
        ref={termsOfServiceRef}
      />
    </>
  );
}
